import React, { FC } from 'react';
import Grid from '@mui/material/Grid';
import Breadcrumb from '@/components/fasce/Breadcrumb';
import ProductDetailTitle from '@/components/fasce/ProductDetailTitle';
import ProductDetailImage from '@/components/fasce/ProductDetailImage';
import ProductOverlayTable from '@/components/fasce/ProductOverlayTable';
import ProductOverlayAccordion from '@/components/fasce/ProductOverlayAccordion';
import { WPLinks_VARIANTE, WPLinks_COLORE, WPLinks_SIZE } from '@/data/wp/operations/queries/fragments/link';
import useTranslation from 'next-translate/useTranslation';
import { Translate } from 'next-translate';
import { buildProductImageSlides, getAngleImages, getTechnicalDataAttributeKey, getTechnicalDataAttributeValue, normalizeAzureAssetUrl } from '@/data/hcl/sdk/utils';
import { AngleImage } from '@/types/common';

interface ProductDetailModalContentProps {
  productData: Record<string, any>;
  breadcrumbs: any;
  attachments: any;
  garanzia: string;
  defaultImage: string;
  defaultThumb: string;
  /** immagini alternative del prodotto padre: fallback se lo sku non ne espone di proprie */
  angleImages?: AngleImage[];
  appUso: string;
};

type ModalData = {
    breadcrumb?: React.ReactElement;
    title?: React.ReactElement;
    slider?: React.ReactElement;
    tables?: React.ReactElement[];
    accordion?: React.ReactElement;
}

const ProductDetailModalContent: FC<ProductDetailModalContentProps> = ({ productData, breadcrumbs, attachments, garanzia, defaultImage, angleImages, appUso }) => {
  // TODO: generare i componenti a partire dalle informazioni in productData
  const {t} = useTranslation('common');
  const modalData: ModalData = getModalData(productData?.data?.productViewFindProductByPartNumber?.catalogEntryView[0], breadcrumbs, attachments, garanzia, defaultImage, angleImages, appUso, productData?.data?.name, t);
  //const modalData: ModalData | null = productData.sku === 'EXAMPLE-VERSIONI' ? MOCK_Versioni: MOCK_Attributi;

  return (<Grid container rowSpacing={0} sx={{ position: 'relative' }}>
    {modalData.breadcrumb &&
      modalData.breadcrumb
    }
    {modalData.title &&
      modalData.title
    }
    <Grid item xs={12} sm={7}>
      {modalData.slider &&
        modalData.slider
      }
      {modalData.tables && modalData.tables.map((singleTable, index) => {
        return (
          <React.Fragment key={index}>
            {singleTable}
          </React.Fragment>
        )
      })}
    </Grid>
    <Grid item xs={12} sm={5}>
      {modalData.accordion &&
        modalData.accordion
      }
    </Grid>
  </Grid>)
}

export default ProductDetailModalContent;

// TODO: rimuovere questi mock a seguito dell'ingegnerizzazione (creare comunque mock della response in \mocks\wcs\graphql\modalProduct.js, vedi anche ProductDetailModal.tsx)



/**
 * Get the value/values from the attribute
 * 
 * @param productData 
 * @param attributeName 
 * @returns 
 */
function getTechnicalDataRowValue(productData: Record<string, any>, attributeName:string){

  let value = '';

  if (productData?.technicalData[attributeName][0]?.values?.length == 1) {
    value = productData?.technicalData[attributeName][0]?.values[0]?.value;
  } else {
    let values:string[] = [] as string[];
    productData?.technicalData[attributeName][0]?.values.forEach((attribute: any) => {
      values.push(attribute.value);
    });
    value = values.join(",");
    
    // TMP --> CLEAN DUPLICATES
    let tmpValues = value.split(",");
    let uniqueValues = tmpValues.filter(function(item, pos) {
      return tmpValues.indexOf(item) == pos;
    });
    value = uniqueValues.join(", ");
    // FINE TMP 
  }

  if (attributeName === 'att_FattoreDiPotenza') {
    value = productData?.technicalData['att_SegnoFattorePotenza'][0]?.values[0]?.value  + ' ' + value;
  }

  if (attributeName === 'att_Verniciatura') {
    value = productData?.technicalData['att_Verniciatura'][0]?.values[0]?.value;
    if (productData?.technicalData['att_Verniciatura2'].length > 0) {
      value = value + ', '+ productData?.technicalData['att_Verniciatura2'][0]?.values[0]?.value;
    }
  }

  if (attributeName === 'att_LivelloEmergenza') {
    value = productData?.technicalData['att_LivelloEmergenza'][0]?.values[0]?.value.concat('%')
  }

  if (attributeName === 'att_Verniciatura2') {
    value = '';
  }

  return value;
}


function getModalData(productData: Record<string, any>, breadcrumbs: any, attachments: any, garanzia: string, defaultImage: string, angleImages: AngleImage[] | undefined, appUso: string, parentProductName: string, t: Translate) {

  productData = Object.assign({}, productData, {selected:false})
  // const newProductData = integrateResponse(productData);
  // productData = Object.assign({}, newProductData, {selected:false})

  productData.iconList = [];

  productData.attachmentsParentProd = attachments;

  // IMMAGINI ALTERNATIVE
  // Si usano quelle dello sku, non quelle del prodotto padre: productViewFindProductByPartNumber
  // restituisce gli attachment del singolo codice, quindi se il PIM differenziera' le immagini per
  // sku la modale mostrera' gia' quelle giuste senza altri interventi.
  // Ad oggi HCL propaga allo sku le stesse immagini del padre, quindi il risultato non cambia.
  // Il fallback sul padre copre il caso in cui la risposta per partNumber non porti gli attachment.
  const skuAngleImages = getAngleImages(productData?.attachments);
  const modalAngleImages = skuAngleImages.length > 0 ? skuAngleImages : angleImages;

  /* let origin = `${window.location.origin.toString()}`;

  console.log('origin: %s',origin)

  let icons = productData?.attachments?.filter((item: { usage: string; }) => item.usage.includes('Pittogrammi'));
  icons?.forEach(async (icon: any) => {
      let iconObj = {
        icon: icon.attachmentAssetPath.replace('https://' + process.env.NEXT_PUBLIC_HCL_HOST_ASSETS,origin),
          iconTooltip: icon.longdesc
      }
      console.log('asset: %s',icon.attachmentAssetPath.replace('https://' + process.env.NEXT_PUBLIC_HCL_HOST_ASSETS,origin))
      productData?.iconList.push(iconObj);
  }); */

  let attributes = productData?.attributes;
  productData.technicalDataRow = [];
  productData.technicalDataAttr = getTechnicalDataAttributeKey();
  productData.technicalData = getTechnicalDataAttributeValue(productData.technicalDataAttr, attributes);

  productData?.technicalDataAttr.forEach((attribute: any) => {
    if(productData?.technicalData[attribute].length > 0) {
      let valueAsDesc = getTechnicalDataRowValue(productData, attribute);
      if(valueAsDesc !== undefined && valueAsDesc.trim() !== '') {
        productData?.technicalDataRow.push({
          title: productData?.technicalData[attribute][0].name,
          desc: valueAsDesc
        });
      }
    }
  });

  productData.attachmentsGrouped = [];
  let attachmentsGroupMapping = ['documentazione-title','disegni-2d-3d-title','fileilluminotecnici-LDT', 'fileilluminotecnici-IES', 'fileilluminotecnici-ULD', 'fileilluminotecnici-ZIP','schedatecnica-title','zipdisegni-title','zipdocumentazione-title','zipfileilluminotecnici-title','ziptutti-title'];
  let accordionsGroupMap: any = {};
  attachmentsGroupMapping.forEach(item => {
    accordionsGroupMap[item] = {
      accordionType: item,
      accordionTitle: t(item),
      accordionRow: [],
      accordionBtn: []
    };
    if(process.env.NEXT_PUBLIC_ENABLE_ZIP === 'true') {
      accordionsGroupMap[item].accordionBtn.push(
        {
          __typename: "DefaultTemplate_Fasce_Fasce_GrayCard_Links_LinkInterno",
          linkInterno: {
            id: "cG9zdDoxNA==",
            slug: "download",
            uri: "/download/",
            title: "download"
          },
          label: t('download-zip', {}, {default: 'Download .zip'}),
          target: null,
          variante: WPLinks_VARIANTE.filledButton,
          colore: WPLinks_COLORE.primary,
          dimensione: WPLinks_SIZE.large
        }
      )
    }
  });

  productData?.attachments?.forEach((attachment: any) => {
    if(attachment.usage === 'BIM'){
      accordionsGroupMap['disegni-2d-3d-title'] = getAccordion(attachment, accordionsGroupMap['disegni-2d-3d-title']);
    }
    if(attachment.usage === 'DisegnoTecnico'){
      accordionsGroupMap['disegni-2d-3d-title'] = getAccordion(attachment, accordionsGroupMap['disegni-2d-3d-title']);
    }
    if(attachment.usage === 'DisegnoTecnico3D'){
      accordionsGroupMap['disegni-2d-3d-title'] = getAccordion(attachment, accordionsGroupMap['disegni-2d-3d-title']);
    }
    if(attachment.usage === 'DisegnoTecnicoJPG'){
      accordionsGroupMap['disegni-2d-3d-title'] = getAccordion(attachment, accordionsGroupMap['disegni-2d-3d-title']);
    }
    if(attachment.usage === 'Modello3D'){
      accordionsGroupMap['disegni-2d-3d-title'] = getAccordion(attachment, accordionsGroupMap['disegni-2d-3d-title']);
    }
    if(attachment.usage === 'ZipDisegni' && process.env.NEXT_PUBLIC_ENABLE_ZIP === 'true'){
      accordionsGroupMap['disegni-2d-3d-title'] = setZipFIleUrl(attachment, accordionsGroupMap['zipdisegni-title']);
    }
    if(attachment.usage === 'IstruzioniMontaggio'){
      accordionsGroupMap['documentazione-title'] = getAccordion(attachment, accordionsGroupMap['documentazione-title']);
    }
    if(attachment.usage === 'ZipDocumentazione' && process.env.NEXT_PUBLIC_ENABLE_ZIP === 'true'){
      accordionsGroupMap['documentazione-title'] = setZipFIleUrl(attachment, accordionsGroupMap['zipdocumentazione-title']);
    }
    if(attachment.usage === 'PhotoElumdat'){
      accordionsGroupMap['fileilluminotecnici-LDT'] = getAccordion(attachment, accordionsGroupMap['fileilluminotecnici-LDT']);
    }
    if(attachment.usage === 'PhotoIesna'){
      accordionsGroupMap['fileilluminotecnici-IES'] = getAccordion(attachment, accordionsGroupMap['fileilluminotecnici-IES']);
    }
    if(attachment.usage === 'ULD'){
      accordionsGroupMap['fileilluminotecnici-ULD'] = getAccordion(attachment, accordionsGroupMap['fileilluminotecnici-ULD']);
    }
    if(attachment.usage === 'ZipFileIlluminotecnici' && process.env.NEXT_PUBLIC_ENABLE_ZIP === 'true'){
       accordionsGroupMap['fileilluminotecnici-ZIP'] = setZipFIleUrl(attachment, accordionsGroupMap['fileilluminotecnici-ZIP']);  //NOT TESTED: mancanza di dati reali 
    }
    if(attachment.usage === 'SchedaTecnica'){
      accordionsGroupMap['documentazione-title'] = getAccordion(attachment, accordionsGroupMap['documentazione-title']);
    }
    if(attachment.usage === 'ZipTutti' && process.env.NEXT_PUBLIC_ENABLE_ZIP === 'true'){
      accordionsGroupMap['ziptutti-title'] = setZipFIleUrl(attachment, accordionsGroupMap['ziptutti-title']);
    }
  });

  // La galleria immagini e' esposta come voce dell'accordion "Documentazione" (in coda alle altre righe).
  // La label e' il nome dello zip prodotto dalla route [product]/[item]/download-image, che costruisce
  // il filename con lo stesso slug del segmento "product" dell'url di dettaglio (id + nome prodotto padre).
  // Senza immagini alternative (ANGLEIMAGES_FULLIMAGE) lo zip conterrebbe solo l'immagine principale: link inutile.
  // if (productData?.partNumber && modalAngleImages && modalAngleImages.length > 0) {
  if (productData?.partNumber) {
    const galleryZipName = 'Pictures.zip';
    accordionsGroupMap['documentazione-title'].accordionRow.push({
      accordionIcon: getFileType(galleryZipName),
      accordionLabel: galleryZipName,
      downloadLink: `${productData?.partNumber}/download-image`,
      directLink: true,
    });
  }

  // --- Raggruppo i file illuminotecnici in un unico accordion ---
  const illuminotecniciGroup = {
    accordionType: 'fileilluminotecnici-title',
    accordionTitle: t('fileilluminotecnici-title', {}, { default: 'File illuminotecnici' }),
    accordionRow: [],
    accordionBtn: [],
  };

  ['fileilluminotecnici-LDT', 'fileilluminotecnici-IES', 'fileilluminotecnici-ULD', 'fileilluminotecnici-ZIP'].forEach((key) => {
    if (accordionsGroupMap[key].accordionRow?.length > 0) {
     (illuminotecniciGroup.accordionRow as any[]).push({
        accordionLabel: key.replace('fileilluminotecnici-', '').toUpperCase(),
        accordionChildren: accordionsGroupMap[key].accordionRow,
  });
    }
     (illuminotecniciGroup.accordionBtn as any[]).push(...accordionsGroupMap[key].accordionBtn);
  });

  attachmentsGroupMapping.forEach((item: string) => {
      if (
    accordionsGroupMap[item].accordionRow?.length > 0 &&
    !item.includes('zip') &&
    !item.includes('fileilluminotecnici-LDT') &&
    !item.includes('fileilluminotecnici-IES') &&
    !item.includes('fileilluminotecnici-ULD') &&
    !item.includes('fileilluminotecnici-ZIP')
  ) {
    productData.attachmentsGrouped.push(accordionsGroupMap[item]);
  }
  if (item === 'disegni-2d-3d-title' && illuminotecniciGroup.accordionRow?.length > 0) {
    productData.attachmentsGrouped.push(illuminotecniciGroup);
  }
    if(item === 'ziptutti-title' && process.env.NEXT_PUBLIC_ENABLE_ZIP === 'true') {
      productData.downloadAllDesktop = [
        {
          __typename: "DefaultTemplate_Fasce_Fasce_GrayCard_Links_LinkInterno",
          linkInterno: {
            id: "cG9zdDoxNA==",
            slug: "download",
            uri: "/download/" + accordionsGroupMap[item].accordionBtn[0].linkInterno.uri + "/download",
            title: "download"
          },
          label: t('download-all-desktop', {}, {default: 'Download tutti i documenti'}),
          target: null,
          variante: WPLinks_VARIANTE.filledButtonIcon,
          colore: WPLinks_COLORE.primary,
          dimensione: WPLinks_SIZE.large,
          icon: 'download'
        }
      ];
      productData.downloadAllMobile = [
        {
          __typename: "DefaultTemplate_Fasce_Fasce_GrayCard_Links_LinkInterno",
          linkInterno: {
            id: "cG9zdDoxNA==",
            slug: "download",
            uri: "/download/" + accordionsGroupMap[item].accordionBtn[0].linkInterno.uri + "/download",
            title: "download"
          },
          label: t('download-all-mobile', {}, {default: 'DOWNLOADS'}),
          target: null,
          variante: WPLinks_VARIANTE.filledButton,
          colore: WPLinks_COLORE.primary,
          dimensione: WPLinks_SIZE.large,
        }
      ];
    }


  });

  let modalData: ModalData = {
    //TODO dinamicizzare breadcrumb (stessa gestione pagina prodotto))
    breadcrumb: <Breadcrumb
    breadcrumbs={breadcrumbs}
  />,
  title: <ProductDetailTitle occhiello={parentProductName.replaceAll('&nbsp;',' ')} title={productData?.partNumber} titleBigMobile />,
  slider: <ProductDetailImage
    slide={buildProductImageSlides(
      productData?.fullImage ? productData?.fullImage : defaultImage,
      modalAngleImages,
      parentProductName?.replaceAll('&nbsp;',' ')
    )}
    showDots={false}
    showPreview
    showArrows
  />,
  tables: [
    <ProductOverlayTable
      key={0}
      title={t('technical-data-title')}
      iconList={productData?.iconList}
      table={productData?.technicalDataRow}
    />
  ],
  //TODO dinamicizzare gli attachments appena arriveranno da PIM (al momento = null per gli SKU)
  accordion: <ProductOverlayAccordion
    title="Downloads"
    accordions={productData.attachmentsGrouped}
    btnDownloadAll={productData.downloadAllDesktop}
    btnDownloadAllMobile={productData.downloadAllMobile}
    downloadGaranzia={{
      garanziaLink: garanzia,
      garanziaIcon: "document",
      garanziaLabel: t('garanzia-prodotto', {}, {default: 'Garanzia prodotto'}),
    }}
    useTag={{
      title: t('applicazioni-duso', {}, {default: "Applicazioni d'uso"}),
      tagList: [appUso]
    }}
  />,
  }
  return modalData;
}

const getAccordion = (atchm: any, accordionTemplate: any) => {
  const isIlluminotecnico =
    accordionTemplate.accordionType.includes('fileilluminotecnici');
  const fileNameForIcon =
    atchm.name?.includes('.') ? atchm.name : atchm.attachmentAssetPath;
  const attach = {
    accordionIcon: getFileType(fileNameForIcon),
    accordionLabel: isIlluminotecnico ? atchm.longdesc || atchm.name : atchm.name,
    downloadLink:
      atchm.identifier.indexOf('  ') > 0
        ? normalizeAzureAssetUrl(atchm.identifier, '/')
        : normalizeAzureAssetUrl(atchm.attachmentAssetPath, '/'),
  };

  accordionTemplate.accordionRow.push(attach);
  accordionTemplate.accordionBtn.push(atchm.attachmentAssetPath);

  return accordionTemplate;
};

const setZipFIleUrl = (atchm: any, accordionTemplate: any) => {
  accordionTemplate.accordionBtn[0].linkInterno.uri = normalizeAzureAssetUrl(atchm.attachmentAssetPath, '/');
  return accordionTemplate;
}

/* const getFileName = (filePath: string) => {
  let filePathSplit = filePath.split('/');
  console.log('name: ' + decodeURIComponent(filePathSplit[filePathSplit.length - 1].split('.')[0]));
  return decodeURIComponent(filePathSplit[filePathSplit.length - 1].split('.')[0]);
} */

const getFileType = (name: string) => {
  if(name !== null){
    // console.debug('name: ', name.split('.')?.pop()?.toLowerCase());
    return name.split('.')?.pop()?.toLowerCase() || 'default';
  }
  return null;
}
